<template>
  <div class="pa-0 ml-auto mr-auto justify-center flex-column profile-notification">
    <div class="form-title mb-3">
      {{ t("Notification") }}
    </div>
    <div>
      {{ t("Please choose your default status notifications for orders.") }}
    </div>
    <div class="profile-notification__wrapper">
      <v-checkbox
        v-for="notification in allNotifications"
        :key="notification.notificationId"
        :model-value="isNotificationChecked(notification)"
        class="profile-notification__checkbox"
        :label="$t(notification.name)"
        :disabled="isForcedForExternalUser(notification)"
        @update:modelValue="(val) => onCheckboxChange(notification, val)"
      />
    </div>
    <div class="d-flex mb-1">
      <Button
        class="mr-3 flex-grow-1"
        color="secondary"
        :scroll="true"
        @click="$emit('goto', 'authentication')"
      >
        <v-icon start>
          mdi-chevron-left
        </v-icon>
        {{ t("Previous") }}
      </Button>

      <Button
        class="flex-grow-1"
        color="primary"
        :scroll="true"
        @click="$emit('goto', 'data-sharing')"
      >
        {{ t("Next") }}
        <v-icon end>
          mdi-chevron-right
        </v-icon>
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watch, reactive, onMounted } from "vue";
import Button from "@/components/input-components/Button.vue";
import type { Notification } from "@/entities/notification";
import { useI18n } from 'vue-i18n'
import { useNotificationSelectionRules } from "@/composables/useNotificationSelectionRules";

const { t } = useI18n()
const props = defineProps<{
  notifications?: Notification[];
  allNotifications: Notification[];
}>();

const {
  isForcedForExternalUser,
} = useNotificationSelectionRules();

const emit = defineEmits<{
  (e: 'addNotification', notification: Notification, options?: { silent?: boolean }): void
  (e: 'removeNotification', notification: Notification, options?: { silent?: boolean }): void
  (e: 'goto', target: string): void
  (e: 'reloadNotifications'): void
}>()

const checkedNotifications = reactive<Record<string, boolean>>({});

function isNotificationChecked(notification: Notification): boolean {
  return !!checkedNotifications[notification.notificationId];
}

// Watch for initially selected notifications

watch(
  () => props.notifications,
  (newValues = []) => {
    for (const n of props.allNotifications ?? []) {
      checkedNotifications[n.notificationId] = false;
    }

    for (const notif of newValues) {
      checkedNotifications[notif.notificationId] = true;
    }
  },
  { immediate: true }
);

// Emit reload on mount
onMounted(() => {
  emit("reloadNotifications");
});

// Checkbox handler
function onCheckboxChange(notification: Notification, isChecked: boolean) {
  if (isForcedForExternalUser(notification)) {
    return;
  }

  checkedNotifications[notification.notificationId] = isChecked;

  const wasPreviouslyChecked = props.notifications?.some(
    (n) => n.notificationId === notification.notificationId
  );

  if (isChecked) {
    emit("addNotification", notification);
  } else if (wasPreviouslyChecked) {
    emit("removeNotification", notification);
  }
}
</script>

<style lang="scss" scoped>
.profile-notification {
  &__wrapper {
    margin-bottom: 1.5rem;
  }

  &__checkbox {
    height: 2rem;
  }
}
</style>
